import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { useNavigate, useParams, Link } from "react-router-dom";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import { saveCareer, updateCareer, getCareers } from "../Apis/CareerAPIs";

const CareerFormLayer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const initialFormState = {
    jobRole: "",
    employmentType: "Full-time",
    workMode: "On-site",
    location: "",
    displayOrder: "",
    openings: "",
    experience: "",
    status: "Active"
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEditing) {
      fetchCareerDetails();
    }
  }, [id]);

  const fetchCareerDetails = async () => {
    setLoading(true);
    const res = await getCareers();
    if (res.status) {
      const data = res.response?.data || res.response;
      const career = data.find(c => (c._id || c.id) === id);
      if (career) {
        setFormData({
          jobRole: career.jobRole || "",
          employmentType: career.employmentType || "Full-time",
          workMode: career.workMode || "On-site",
          location: career.location || "",
          displayOrder: career.displayOrder ?? 0,
          openings: career.openings || "",
          experience: career.experience || "",
          status: career.status || "Active"
        });
      }
    }
    setLoading(false);
  };

  const formatNumberWithCommas = (value) => {
    if (!value) return "";
    const cleanValue = value.replace(/,/g, "");
    return cleanValue.replace(/\d+/g, (n) => {
        return n.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.jobRole.trim()) newErrors.jobRole = "Job Role is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (formData.displayOrder === "") newErrors.displayOrder = "Display Order is required";
    if (!formData.openings) newErrors.openings = "Number of openings is required";
    if (!formData.experience.trim()) newErrors.experience = "Experience is required";
    if (!formData.employmentType.trim()) newErrors.employmentType = "Employment Type is required";
    if (!formData.workMode.trim()) newErrors.workMode = "Work Mode is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "openings" || name === "displayOrder") {
        // Allow only digits
        const numericValue = value.replace(/[^0-9]/g, "");
        setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : "" }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setIsUpdating(true);
    const payload = {
      ...formData,
      openings: parseInt(formData.openings),
      displayOrder: parseInt(formData.displayOrder) || 0
    };

    let res;
    if (isEditing) {
      res = await updateCareer(id, payload);
    } else {
      res = await saveCareer(payload);
    }

    if (res.status) {
      toast.success(isEditing ? "Career Updated Successfully" : "Career Added Successfully");
      navigate("/careers");
    }
    setIsUpdating(false);
  };

  if (loading) return (
    <div className="d-flex justify-content-center py-50">
      <div className="spinner-border text-danger" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className="card radius-12 border-0 shadow-sm overflow-hidden">
      <div 
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
        style={{
          background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
        }}
      >
        <h6 className="text-white mb-0" style={{ fontWeight: "700" }}>
          {isEditing ? "EDIT CAREER OPENING" : "ADD CAREER OPENING"}
        </h6>
        <Link
          to="/careers"
          className="btn btn-sm d-flex align-items-center gap-2"
          style={{
            backgroundColor: "#f7e7da",
            color: "black",
            fontWeight: "600",
          }}
        >
          <Icon icon="solar:arrow-left-bold" /> List Careers
        </Link>
      </div>
      <div className="card-body p-24">
        <form onSubmit={handleSubmit} noValidate>
          <div className="row g-4">
            <div className="col-md-6 text-start">
              <label className="form-label fw-bold">Job Role <span className="text-danger">*</span></label>
              <input
                type="text"
                name="jobRole"
                className={`form-control radius-8 ${errors.jobRole ? "is-invalid" : ""}`}
                placeholder="e.g. Event Specialist"
                value={formData.jobRole}
                onChange={handleInputChange}
                style={{ height: "44px" }}
              />
              {errors.jobRole && <div className="invalid-feedback">{errors.jobRole}</div>}
            </div>
            
            <div className="col-md-6 text-start">
              <label className="form-label fw-bold">Location <span className="text-danger">*</span></label>
              <input
                type="text"
                name="location"
                className={`form-control radius-8 ${errors.location ? "is-invalid" : ""}`}
                placeholder="e.g. Chennai"
                value={formData.location}
                onChange={handleInputChange}
                style={{ height: "44px" }}
              />
              {errors.location && <div className="invalid-feedback">{errors.location}</div>}
            </div>

            <div className="col-md-4 text-start">
              <label className="form-label fw-bold">Employment Type <span className="text-danger">*</span></label>
              <div className="input-group has-validation">
                <span className="input-group-text bg-base border-end-0 radius-start-8 text-secondary-light">
                  <Icon icon="solar:clock-circle-linear" className="text-xl" />
                </span>
                <div className="flex-grow-1">
                  <Select
                    name="employmentType"
                    options={[
                        { value: "Full-time", label: "Full-time" },
                        { value: "Part-time", label: "Part-time" },
                        { value: "Internship", label: "Internship" },
                        { value: "Contract", label: "Contract" }
                    ]}
                    styles={selectStyles({ 
                        error: !!errors.employmentType,
                        custom: { 
                            control: (base) => ({ ...base, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }) 
                        } 
                    })}
                    value={[
                        { value: "Full-time", label: "Full-time" },
                        { value: "Part-time", label: "Part-time" },
                        { value: "Internship", label: "Internship" },
                        { value: "Contract", label: "Contract" }
                    ].find(opt => opt.value === formData.employmentType)}
                    onChange={(opt) => handleSelectChange(opt, { name: "employmentType" })}
                    placeholder="Select Type"
                  />
                </div>
                {errors.employmentType && <div className="invalid-feedback d-block">{errors.employmentType}</div>}
              </div>
            </div>

            <div className="col-md-4 text-start">
              <label className="form-label fw-bold">Work Mode <span className="text-danger">*</span></label>
              <div className="input-group has-validation">
                <span className="input-group-text bg-base border-end-0 radius-start-8 text-secondary-light">
                  <Icon icon="solar:laptop-minimalistic-linear" className="text-xl" />
                </span>
                <div className="flex-grow-1">
                    <Select
                        name="workMode"
                        options={[
                            { value: "On-site", label: "On-site" },
                            { value: "Remote", label: "Remote" },
                            { value: "Hybrid", label: "Hybrid" }
                        ]}
                        styles={selectStyles({ 
                            error: !!errors.workMode,
                            custom: { 
                                control: (base) => ({ ...base, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }) 
                            } 
                        })}
                        value={[
                            { value: "On-site", label: "On-site" },
                            { value: "Remote", label: "Remote" },
                            { value: "Hybrid", label: "Hybrid" }
                        ].find(opt => opt.value === formData.workMode)}
                        onChange={(opt) => handleSelectChange(opt, { name: "workMode" })}
                        placeholder="Select Mode"
                    />
                </div>
                {errors.workMode && <div className="invalid-feedback d-block">{errors.workMode}</div>}
              </div>
            </div>

            <div className="col-md-4 text-start">
              <label className="form-label fw-bold">Status <span className="text-danger">*</span></label>
              <Select
                name="status"
                options={[
                    { value: "Active", label: "Active" },
                    { value: "Inactive", label: "Inactive" }
                ]}
                styles={selectStyles(!!errors.status)}
                value={[
                    { value: "Active", label: "Active" },
                    { value: "Inactive", label: "Inactive" }
                ].find(opt => opt.value === formData.status)}
                onChange={(opt) => handleSelectChange(opt, { name: "status" })}
                placeholder="Select Status"
              />
              {errors.status && <div className="invalid-feedback d-block">{errors.status}</div>}
            </div>

            <div className="col-md-4 text-start">
              <label className="form-label fw-bold">Display Order <span className="text-danger">*</span></label>
              <input
                type="number"
                name="displayOrder"
                className={`form-control radius-8 ${errors.displayOrder ? "is-invalid" : ""}`}
                placeholder="e.g. 1"
                value={formData.displayOrder}
                onChange={handleInputChange}
                min="0"
                style={{ height: "44px" }}
              />
              {errors.displayOrder && <div className="invalid-feedback">{errors.displayOrder}</div>}
            </div>

            <div className="col-md-4 text-start">
              <label className="form-label fw-bold">Experience <span className="text-danger">*</span></label>
              <input
                type="text"
                name="experience"
                className={`form-control radius-8 ${errors.experience ? "is-invalid" : ""}`}
                placeholder="e.g. 2+ Years"
                value={formData.experience}
                onChange={handleInputChange}
                style={{ height: "44px" }}
              />
              {errors.experience && <div className="invalid-feedback">{errors.experience}</div>}
            </div>

            <div className="col-md-4 text-start">
              <label className="form-label fw-bold">Openings <span className="text-danger">*</span></label>
              <input
                type="number"
                name="openings"
                className={`form-control radius-8 ${errors.openings ? "is-invalid" : ""}`}
                placeholder="e.g. 2"
                value={formData.openings}
                onChange={handleInputChange}
                min="0"
                style={{ height: "44px" }}
              />
              {errors.openings && <div className="invalid-feedback">{errors.openings}</div>}
            </div>
          </div>
          
          <div className="row g-4 mt-12">
            <div className="col-12 mt-40 pt-24 border-top d-flex justify-content-end gap-3">
                <button 
                type="button" 
                className="btn radius-8 px-40 fw-bold border-danger text-danger bg-white" 
                onClick={() => navigate("/careers")}
                style={{ height: "44px" }}
                >
                Cancel
                </button>
                <button 
                type="submit"
                className="btn px-40 radius-8 border-0 fw-bold d-flex align-items-center gap-2" 
                disabled={isUpdating} 
                style={{ backgroundColor: "#ed1c24", color: "white", height: "44px" }}
                >
                {isUpdating ? (
                    <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Saving...
                    </>
                ) : (
                    <>
                    <Icon icon="solar:diskette-bold" className="text-xl" />
                    {isEditing ? "Update Career" : "Save Career"}
                    </>
                )}
                </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CareerFormLayer;
